import { clarity } from "react-microsoft-clarity";

type UploadStats = {
  totalFiles: number;
  totalMegabytes: number;
};

type EventTimingPhase = "before_event" | "during_event" | "after_event";
type TimeOfDay = "morning" | "afternoon" | "night";

/**
 * Sets Clarity tags for upload behavior based on file count and size ranges
 * @param uploadStats - Upload statistics from Guest store
 */
export const setUploadBehaviorTags = (uploadStats: UploadStats) => {
  if (!clarity.hasStarted() || uploadStats.totalFiles === 0) {
    return;
  }

  // Set file count range tag
  const filesRange = getUploadFilesRange(uploadStats.totalFiles);
  if (filesRange) {
    clarity.setTag("upload_files_range", filesRange);
  }

  // Set size range tag
  const sizeRange = getUploadSizeRange(uploadStats.totalMegabytes);
  if (sizeRange) {
    clarity.setTag("upload_size_range", sizeRange);
  }
};

/**
 * Determines the file count range category
 * @param totalFiles - Total number of files uploaded
 * @returns Range category string
 */
export const getUploadFilesRange = (totalFiles: number): string | null => {
  if (totalFiles === 0) return null;

  if (totalFiles >= 1 && totalFiles <= 5) {
    return "1-5";
  } else if (totalFiles >= 6 && totalFiles <= 20) {
    return "6-20";
  } else if (totalFiles > 20) {
    return "20+";
  }

  return null;
};

/**
 * Determines the upload size range category
 * @param totalMegabytes - Total megabytes uploaded
 * @returns Range category string
 */
export const getUploadSizeRange = (totalMegabytes: number): string | null => {
  if (totalMegabytes === 0) return null;

  if (totalMegabytes < 200) {
    return "<200mb";
  } else if (totalMegabytes >= 200 && totalMegabytes < 500) {
    return "200mb-500mb";
  } else if (totalMegabytes >= 500 && totalMegabytes < 1024) {
    return "500mb-1gb";
  } else if (totalMegabytes >= 1024) {
    return "1gb+";
  }

  return null;
};

/**
 * Determines the event timing phase based on event date
 * @param eventDateString - Event date in ISO format (e.g., "2025-11-26")
 * @returns Event timing phase
 *
 * Note: "during_event" covers the event day and the following day (48-hour window)
 */
export const getEventTimingPhase = (eventDateString: string): EventTimingPhase => {
  const eventDate = new Date(eventDateString);
  const today = new Date();

  // Normalize dates to midnight for accurate day comparison
  const eventDateMidnight = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
  const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  // Calculate the day after event
  const dayAfterEventMidnight = new Date(eventDateMidnight);
  dayAfterEventMidnight.setDate(dayAfterEventMidnight.getDate() + 1);

  if (todayMidnight < eventDateMidnight) {
    return "before_event";
  } else if (todayMidnight.getTime() >= eventDateMidnight.getTime() && todayMidnight.getTime() <= dayAfterEventMidnight.getTime()) {
    return "during_event";
  } else {
    return "after_event";
  }
};

/**
 * Sets Clarity tag for event timing phase
 * @param eventDateString - Event date in ISO format
 */
export const setEventTimingTag = (eventDateString: string) => {
  if (!clarity.hasStarted() || !eventDateString) {
    return;
  }

  const phase = getEventTimingPhase(eventDateString);
  clarity.setTag("event_timing", phase);
};

/**
 * Determines the time of day based on current hour
 * @returns Time of day category
 *
 * Time ranges:
 * - Morning: 6:00 AM - 11:59 AM
 * - Afternoon: 12:00 PM - 5:59 PM
 * - Night: 6:00 PM - 5:59 AM
 */
export const getTimeOfDay = (): TimeOfDay => {
  const hour = new Date().getHours();

  if (hour >= 6 && hour < 12) {
    return "morning";
  } else if (hour >= 12 && hour < 18) {
    return "afternoon";
  } else {
    return "night";
  }
};

/**
 * Sets Clarity tag for time of day
 */
export const setTimeOfDayTag = () => {
  if (!clarity.hasStarted()) {
    return;
  }

  const timeOfDay = getTimeOfDay();
  clarity.setTag("time_of_day", timeOfDay);
};
